/**
 * WebSocket client with auto-reconnect + exponential backoff.
 *
 * SLA-Anforderung Phase 1: max 5s zwischen Reconnect-Versuchen — also begrenzen wir
 * den Backoff entsprechend.
 */

export type ConnectionStatus = 'connecting' | 'open' | 'closed';

export interface ChatStreamHandlers {
  onMessage: (event: unknown) => void;
  onChatEvent: (event: unknown) => void;
  onChannelEvent?: (event: unknown) => void;
  onStatus: (status: ConnectionStatus) => void;
  onStreamOnline?: () => void;
  onChatReset?: () => void;
}

const INITIAL_BACKOFF_MS = 500;
const MAX_BACKOFF_MS = 5_000; // SLA-Anforderung
const PING_INTERVAL_MS = 25_000;

function resolveWsUrl(): string {
  if (typeof window === 'undefined') return '';
  const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  // Dev: Vite läuft auf 5173, Backend auf 3030 — explizit verbinden.
  if (window.location.port === '5173') {
    return `${proto}//${window.location.hostname}:3030/ws`;
  }
  // Prod: same-origin, Reverse-Proxy routet /ws → chat-core
  return `${proto}//${window.location.host}/ws`;
}

export class ChatStream {
  private ws: WebSocket | null = null;
  private backoffMs = INITIAL_BACKOFF_MS;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private pingTimer: ReturnType<typeof setInterval> | null = null;
  private stopped = false;
  private readonly handlers: ChatStreamHandlers;
  private readonly url: string;

  constructor(handlers: ChatStreamHandlers, url?: string) {
    this.handlers = handlers;
    this.url = url ?? resolveWsUrl();
  }

  connect(): void {
    this.stopped = false;
    this.open();
  }

  disconnect(): void {
    this.stopped = true;
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    if (this.pingTimer) {
      clearInterval(this.pingTimer);
      this.pingTimer = null;
    }
    if (this.ws) {
      try {
        this.ws.close(1000, 'client shutdown');
      } catch {
        // ignore
      }
      this.ws = null;
    }
    this.handlers.onStatus('closed');
  }

  private open(): void {
    if (this.stopped) return;
    this.handlers.onStatus('connecting');

    let ws: WebSocket;
    try {
      ws = new WebSocket(this.url);
    } catch (err) {
      console.error('WS construct failed', err);
      this.scheduleReconnect();
      return;
    }
    this.ws = ws;

    ws.onopen = () => {
      this.backoffMs = INITIAL_BACKOFF_MS;
      this.handlers.onStatus('open');
      // Heartbeat: text "ping" → server replies "pong"
      if (this.pingTimer) clearInterval(this.pingTimer);
      this.pingTimer = setInterval(() => {
        try {
          if (ws.readyState === WebSocket.OPEN) ws.send('ping');
        } catch {
          // ignore — close handler will trigger reconnect
        }
      }, PING_INTERVAL_MS);
    };

    ws.onmessage = (ev) => {
      const data = ev.data;
      if (typeof data !== 'string') return;
      if (data === 'pong') return;
      let parsed: unknown;
      try {
        parsed = JSON.parse(data);
      } catch {
        return;
      }
      if (!parsed || typeof parsed !== 'object') return;
      const frame = parsed as { type?: string; data?: unknown };
      if (frame.type === 'message') {
        this.handlers.onMessage(frame.data);
      } else if (frame.type === 'event') {
        this.handlers.onChatEvent(frame.data);
      } else if (frame.type === 'channel_event') {
        this.handlers.onChannelEvent?.(frame.data);
      } else if (frame.type === 'stream_online') {
        this.handlers.onStreamOnline?.();
      } else if (frame.type === 'chat_reset') {
        this.handlers.onChatReset?.();
      }
      // 'hello' frames are noise for the UI but useful in devtools
    };

    ws.onerror = () => {
      // close handler will run after error; nothing to do here
    };

    ws.onclose = () => {
      if (this.pingTimer) {
        clearInterval(this.pingTimer);
        this.pingTimer = null;
      }
      if (this.ws === ws) this.ws = null;
      this.handlers.onStatus('closed');
      this.scheduleReconnect();
    };
  }

  private scheduleReconnect(): void {
    if (this.stopped) return;
    if (this.reconnectTimer) return;
    const jitter = Math.floor(Math.random() * 250);
    const delay = Math.min(this.backoffMs + jitter, MAX_BACKOFF_MS);
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this.backoffMs = Math.min(this.backoffMs * 2, MAX_BACKOFF_MS);
      this.open();
    }, delay);
  }
}
